import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, CheckCheck, Phone, PhoneOff, PhoneMissed, Camera } from "lucide-react";
import { VoiceNotePlayer } from "./VoiceNotePlayer";
import ReactMarkdown from "react-markdown";

// Render markdown content with proper styling
function MarkdownContent({ text, isOwn }: { text: string; isOwn: boolean }) {
  // Convert tracking codes and plain URLs to clickable links
  const processed = text
    .replace(/\b(MLL[A-Z0-9_]+)\b/g, (match) => `[${match}](${window.location.origin}/tracking/${match})`)
    .replace(/\b(manchesterluxuryladies\.com\S*)/gi, (match) => `[${match}](https://${match})`)
    .replace(/(?<!\[)\b(https?:\/\/[^\s)]+)/g, (match) => `[${match}](${match})`);

  return (
    <ReactMarkdown
      components={{
        p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
        strong: ({ children }) => <strong className="font-bold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer"
            className={`underline underline-offset-2 hover:opacity-80 break-all ${isOwn ? "text-black/80" : "text-[#D4AF37]"}`}
            onClick={e => e.stopPropagation()}>
            {children}
          </a>
        ),
        ul: ({ children }) => <ul className="list-disc pl-4 my-1 space-y-0.5">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal pl-4 my-1 space-y-0.5">{children}</ol>,
        li: ({ children }) => <li>{children}</li>,
        table: ({ children }) => (
          <div className="overflow-x-auto my-2">
            <table className={`text-[10px] border-collapse w-full ${isOwn ? "border-black/20" : "border-gray-700"}`}>{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className={isOwn ? "border-b border-black/20" : "border-b border-gray-700"}>{children}</thead>,
        th: ({ children }) => <th className="text-left px-2 py-1 font-bold">{children}</th>,
        td: ({ children }) => <td className={`px-2 py-1 ${isOwn ? "border-t border-black/10" : "border-t border-gray-800"}`}>{children}</td>,
        h1: ({ children }) => <p className="font-bold text-sm mb-1">{children}</p>,
        h2: ({ children }) => <p className="font-bold text-xs mb-1">{children}</p>,
        h3: ({ children }) => <p className="font-semibold text-xs mb-0.5">{children}</p>,
        code: ({ children }) => <code className={`px-1 py-0.5 rounded text-[10px] ${isOwn ? "bg-black/10" : "bg-gray-800"}`}>{children}</code>,
        hr: () => <hr className={`my-2 ${isOwn ? "border-black/20" : "border-gray-700"}`} />,
      }}
    >
      {processed}
    </ReactMarkdown>
  );
}

interface MessageBubbleProps {
  message: {
    id: string;
    senderRole: string;
    messageType: string;
    content: string | null;
    attachmentUrl?: string | null;
    attachmentDuration?: number | null;
    status: string;
    createdAt: string;
  };
  isOwn: boolean;
}

function ImageMessage({ url, isDisappearing, timestamp, isOwn, isVideo = false }: { url: string; isDisappearing: boolean; timestamp: string; isOwn: boolean; isVideo?: boolean }) {
  const [revealed, setRevealed] = useState(false);
  const [expired, setExpired] = useState(false);
  const [countdown, setCountdown] = useState(9);

  useEffect(() => {
    if (!revealed || !isDisappearing) return;
    const interval = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(interval);
          setExpired(true);
          setRevealed(false);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [revealed, isDisappearing]);

  if (expired) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
        <div className="px-4 py-3 bg-[#1A1A1A] border border-gray-800 rounded-2xl text-[10px] text-gray-500 italic flex items-center gap-2">
          <Camera size={12} /> Photo expired
          <span className="text-[8px] text-gray-600 ml-2">
            {new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] rounded-2xl overflow-hidden ${isOwn ? "bg-[#D4AF37]" : "bg-[#1A1A1A] border border-gray-800"}`}
        style={{ userSelect: "none", WebkitTouchCallout: "none" } as any}
      >
        {isDisappearing && !revealed ? (
          <button
            type="button"
            onClick={() => { setRevealed(true); setCountdown(9); }}
            className="w-48 h-48 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-gray-800 to-gray-900"
          >
            <Camera size={24} className="text-[#D4AF37]" />
            <span className="text-[10px] text-gray-400">Tap to view {isVideo ? "video" : "photo"}</span>
            <span className="text-[8px] text-gray-600">Disappears in 9s</span>
          </button>
        ) : (
          <div className="relative">
            {isVideo ? (
              <video
                src={url}
                className="w-48 sm:w-64 rounded-t-2xl"
                controls
                controlsList="nodownload"
                onContextMenu={e => e.preventDefault()}
              />
            ) : (
              <img
                src={url}
                alt="Shared photo"
                className="w-48 sm:w-64 object-cover rounded-t-2xl"
                draggable={false}
                onContextMenu={e => e.preventDefault()}
              />
            )}
            {isDisappearing && revealed && (
              <div className="absolute top-2 right-2 bg-black/70 text-white text-[10px] px-2 py-1 rounded-full">
                {countdown}s
              </div>
            )}
          </div>
        )}
        <div className="px-3 py-1.5 flex items-center gap-1">
          <span className={`text-[8px] ${isOwn ? "text-black/50" : "text-gray-600"}`}>
            {new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
          {isDisappearing && (
            <span className={`text-[8px] ${isOwn ? "text-black/40" : "text-gray-600"}`}>
              · disappearing
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function formatCallDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds < 1) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function MessageBubble({ message, isOwn }: MessageBubbleProps) {
  // System messages: centered, gray, italic
  if (message.messageType === "system") {
    return (
      <div className="flex justify-center my-2">
        <span className="text-[10px] text-gray-500 italic px-4 py-1 bg-[#1A1A1A] rounded-full">
          {message.content}
        </span>
      </div>
    );
  }

  // Image and video messages
  if ((message.messageType === "image" || message.messageType === "video") && message.attachmentUrl) {
    const isDisappearing = message.content === "disappearing";
    return <ImageMessage url={message.attachmentUrl} isDisappearing={isDisappearing} timestamp={message.createdAt} isOwn={isOwn} isVideo={message.messageType === "video"} />;
  }

  // Call log messages: centered, styled like WhatsApp call entries
  if (message.messageType === "call_log") {
    const duration = message.attachmentDuration;
    const wasAnswered = duration && duration > 0;
    const isMissed = message.content?.includes("missed") || message.content?.includes("declined");

    return (
      <div className="flex justify-center my-3">
        <div className="flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-gray-800 rounded-full">
          {isMissed ? (
            <PhoneMissed size={12} className="text-red-400" />
          ) : wasAnswered ? (
            <Phone size={12} className="text-emerald-400" />
          ) : (
            <PhoneOff size={12} className="text-gray-500" />
          )}
          <span className={`text-[10px] ${isMissed ? "text-red-400" : wasAnswered ? "text-emerald-400" : "text-gray-500"}`}>
            {wasAnswered ? `Voice call · ${formatCallDuration(duration)}` : message.content || "Call ended"}
          </span>
          <span className="text-[8px] text-gray-600">
            {new Date(message.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
      </div>
    );
  }

  const isAI = message.senderRole === "ai";
  const isAgent = message.senderRole === "agent";
  const isCompanion = message.senderRole === "companion";
  const isDriver = message.senderRole === "driver";

  // Role-based bubble colors
  const bubbleClass = isAI
    ? "bg-blue-500/20 text-blue-100 border border-blue-500/30"
    : isCompanion
    ? "bg-purple-500/20 text-purple-100 border border-purple-500/30"
    : isDriver
    ? "bg-cyan-500/20 text-cyan-100 border border-cyan-500/30"
    : isOwn
    ? "bg-[#D4AF37] text-black font-medium"
    : "bg-[#1A1A1A] text-gray-300 border border-gray-800";

  // Role labels
  const roleLabel = isAI ? "AI Assistant"
    : isCompanion ? "Companion"
    : isDriver ? "Driver"
    : isAgent ? "Admin"
    : null;

  const roleLabelColor = isAI ? "text-blue-400"
    : isCompanion ? "text-purple-400"
    : isDriver ? "text-cyan-400"
    : isAgent ? "text-[#D4AF37]"
    : "";

  // Align: own messages right, others left. AI/agent always right in customer view
  const alignRight = isOwn || isAI;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${alignRight ? "justify-end" : "justify-start"}`}
    >
      <div className={`max-w-[80%] p-3 rounded-2xl text-xs leading-relaxed ${bubbleClass}`}>
        {/* Role label for non-customer messages in group chats */}
        {roleLabel && (
          <span className={`text-[8px] ${roleLabelColor} uppercase tracking-widest block mb-1`}>{roleLabel}</span>
        )}

        {/* Voice note */}
        {message.messageType === "voice_note" && message.attachmentUrl ? (
          <VoiceNotePlayer
            url={message.attachmentUrl}
            duration={message.attachmentDuration || 0}
            isOwn={isOwn}
          />
        ) : (
          <MarkdownContent text={message.content || ""} isOwn={isOwn} />
        )}

        {/* Timestamp + read receipts */}
        <div className={`flex items-center gap-1 mt-1 ${alignRight ? "justify-end" : ""}`}>
          <span className={`text-[8px] ${isOwn ? "text-black/50" : "text-gray-600"}`}>
            {new Date(message.createdAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
          {(isOwn || isAI || isAgent) && (
            message.status === "read" ? (
              <CheckCheck size={10} className="text-emerald-400" />
            ) : (
              <Check size={10} className={isOwn ? "text-black/50" : "text-gray-500"} />
            )
          )}
        </div>
      </div>
    </motion.div>
  );
}
